import { isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import {
  selectOptionCurrency,
  selectOptionLegsProperties,
  selectOptionLegsValidation,
} from '@/store/option/selectors/optionSelectors.ts';
import type { LegId } from '@/store/option/state.ts';

type NotionalProps = {
  tileId: string;
};

export function Notional({ tileId }: NotionalProps) {
  const [checkOption] = useCheckOption();
  const legId: LegId = `${tileId}/0`;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { notional, notionalCurrencyIndex, notionalCurrency, validation } = useOptionSelector((option) => {
    const { currency1, currency2 } = selectOptionCurrency(option);
    const legProperties = selectOptionLegsProperties(option, legId);
    const validation = selectOptionLegsValidation(option, legId)?.notional ?? [];

    return {
      notional: legProperties?.notional,
      notionalCurrencyIndex: legProperties?.notionalCurrencyIndex,
      notionalCurrency: legProperties?.notionalCurrencyIndex === 2 ? currency2 : currency1,
      validation,
    };
  });

  const handleAmountChange = (notional: number | undefined) => {
    sgwtWebAnalytics?.trackEvent('Option', 'Notional changed', String(notional));
    checkOption({
      tileId,
      legProperties: {
        legId,
        notional,
        notionalCurrencyIndex,
      },
    });
  };

  const handleAmountCurrencyChange = () => {
    sgwtWebAnalytics?.trackEvent(
      'Option',
      'Notional currency changed',
      notionalCurrencyIndex === 1 ? 'Currency2' : 'Currency1',
    );
    if (isNotDefined(notional)) {
      return;
    }

    checkOption({
      tileId,
      legProperties: {
        legId: `${tileId}/0`,
        notional,
        notionalCurrencyIndex: notionalCurrencyIndex === 1 ? 2 : 1,
      },
    });
  };

  return (
    <OptionFormGroup label={<FormattedMessage id="option.fields.notional" />}>
      <TradeCaptureValidationWrapper validation={validation}>
        <SgAmountInput
          onUnitClick={handleAmountCurrencyChange}
          value={notional}
          unit={notionalCurrency}
          size="sm"
          data-e2e="notional"
          onChange={handleAmountChange}
          unitBackgroundColor={notionalCurrencyIndex === 2 ? 'warning' : undefined}
          placeholder="Notional"
        />
      </TradeCaptureValidationWrapper>
    </OptionFormGroup>
  );
}
